import { useState, useEffect, useMemo } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Rec helpers ──────────────────────────────────────────────

const DELETE_RECS = ['DELETE', 'REMOVE', 'STOP', 'REMOVEUNUSEDRESOURCE'];

const resolveRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (DELETE_RECS.includes(t))                                              recPillClass = 'rec-id';
  else if (['DOWNGRADE', 'RIGHTSIZE', 'DOWNSIZE', 'CONSOLIDATE'].includes(t)) recPillClass = 'rec-rs';
  else if (['SCHEDULE', 'SCHEDULESTART', 'SCHEDULESTOP'].includes(t))         recPillClass = 'rec-warn';
  return { recType: t || 'OPTIMAL', recPillClass };
};

const notNA = (v) => (v != null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA') ? v : null;
const toNum  = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

const TIER_COLORS = {
  PremiumV3: '#8e24aa', Premium: '#8e24aa', Standard: '#0078d4',
  Basic: '#2ea66f', Free: '#94a3b8', Shared: '#26c6da', Unknown: '#9aa3b7',
};

// ── Row Mapper ────────────────────────────────────────────────

const toWebAppRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? (saving > 0 ? 'DOWNGRADE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveRec(rawRec);

  // Show backend detail exactly; NA / absent falls through, finally "—".
  const remark = notNA(item.recommendationDetail) ?? notNA(item.remark) ?? '—';

  return {
    id:                   item.id,
    appName:              item.name ?? item.appName ?? item.siteName ?? '—',
    location:             item.location ?? '—',
    resourceGroup:        item.resourceGroup ?? '—',
    osType:               item.osType ?? '—',
    runtime:              item.runtime ?? '—',
    kind:                 item.kind ?? '—',
    sku:                  item.sku ?? '—',
    planName:             item.planName ?? '—',
    planTier:             item.planTier ?? item.sku ?? '—',
    planSkuName:          item.planSkuName ?? item.planSize ?? '—',
    planCapacity:         toNum(item.planCapacity),
    planNumberOfSites:    toNum(item.planNumberOfSites),
    recommendedSkuName:   item.recommendedSkuName ?? '—',
    state:                item.state ?? '—',
    usageState:           item.usageState ?? '—',
    avgCpu:               toNum(item.averageCpuValue),
    peakCpu:              toNum(item.peakCpuValue),
    lastModified:         item.lastModifiedTimeUtc ?? null,
    cost:                 `$${current.toFixed(2)}`,
    costValue:            current,
    recommendedCost:      `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving:               formatSavingStr(saving),
    savingValue:          saving,
    actionLabel:          'Assist with AI',
    actionClass:          'mini-btn ai-assist',
  };
};

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent  = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec      = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving   = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const planCount   = new Set(rows.map((r) => r.planName).filter((p) => p && p !== '—')).size;
  const stoppedRows = rows.filter((r) => (r.state ?? '').toLowerCase() === 'stopped' || DELETE_RECS.includes(r.recType));
  const stoppedCost = stoppedRows.reduce((s, r) => s + r.costValue, 0);

  return buildBaseCostKpis({
    countTitle:    'Web Apps',
    countValue:    rows.length,
    countSubtitle: `across ${planCount} App Service plan${planCount !== 1 ? 's' : ''}`,
    current, saving, recommended,
    kpi4Title:    'Stopped / Idle Apps',
    kpi4Value:    `${stoppedRows.length}`,
    kpi4Subtitle: `$${stoppedCost.toFixed(2)}/mo reclaimable`,
  });
};

// ── Charts ────────────────────────────────────────────────────

// CPU headroom — avg vs peak CPU per app (real averageCpuValue / peakCpuValue).
const buildCpuHeadroom = (rows) => {
  const named = rows
    .filter((r) => r.avgCpu != null || r.peakCpu != null)
    .sort((a, b) => b.costValue - a.costValue)
    .slice(0, 8);
  if (!named.length) return null;
  return {
    labels: named.map((r) => (r.appName.length > 16 ? r.appName.slice(0, 15) + '…' : r.appName)),
    datasets: [
      { label: 'Avg CPU %',  data: named.map((r) => r.avgCpu  ?? 0), backgroundColor: '#0078d4', borderRadius: 3, barPercentage: 0.75 },
      { label: 'Peak CPU %', data: named.map((r) => r.peakCpu ?? 0), backgroundColor: '#f57c00', borderRadius: 3, barPercentage: 0.75 },
    ],
  };
};

const buildTierDonut = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const buckets = {};
  rows.forEach((r) => {
    const t = r.planTier && r.planTier !== '—' ? r.planTier : 'Unknown';
    buckets[t] = (buckets[t] || 0) + (hasCost ? r.costValue : 1);
  });
  const labels = Object.keys(buckets);
  if (!labels.length) return null;
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: labels.map((k) => TIER_COLORS[k] ?? TIER_COLORS.Unknown),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildLocationBar = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const byLoc = {};
  rows.forEach((r) => {
    if (!r.location || r.location === '—') return;
    byLoc[r.location] = (byLoc[r.location] || 0) + (hasCost ? r.costValue : 1);
  });
  const sorted = Object.entries(byLoc).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.length) return null;
  return {
    _hasCost: hasCost,
    labels: sorted.map(([k]) => k),
    datasets: [{
      label: hasCost ? 'Cost ($)' : 'Apps',
      data: sorted.map(([, v]) => parseFloat(v.toFixed(2))),
      backgroundColor: '#0078d4',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Downgrade plans': 0, 'Consolidate apps': 0, 'Delete / stop idle': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (['CONSOLIDATE'].includes(r.recType)) buckets['Consolidate apps'] += s;
    else if (DELETE_RECS.includes(r.recType)) buckets['Delete / stop idle'] += s;
    else buckets['Downgrade plans'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#f57c00', '#0078d4', '#ef4444'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows.filter((r) => r.savingValue > 0).sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (['CONSOLIDATE'].includes(r.recType)) actionLabel = 'Consolidate →';
    else if (DELETE_RECS.includes(r.recType)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (['DOWNGRADE', 'DOWNSIZE', 'RIGHTSIZE'].includes(r.recType)) actionLabel = 'Downgrade →';
    return { id: i + 1, title: r.appName || r.id, amount: r.saving, desc: `${r.planTier} · ${r.location}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Cells ─────────────────────────────────────────────────────

const CpuUtilCell = ({ row }) => {
  if (row.avgCpu == null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  const pct   = Math.min(100, Math.max(0, row.avgCpu));
  const color = pct < 35 ? '#2ea66f' : pct < 70 ? '#f57c00' : '#ef4444';
  const title = row.peakCpu != null ? `Peak ${row.peakCpu}%` : undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }} title={title}>
      <div style={{ flex: 1, height: 6, background: '#eef1f7', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{pct.toFixed(0)}%</span>
    </div>
  );
};

const StatusCell = ({ row }) => {
  const ok = (row.state ?? '').toLowerCase() === 'running';
  return <span className={`status-pill ${ok ? 'st-running' : 'st-stopped'}`}>{row.state}</span>;
};

// ── Table Columns ─────────────────────────────────────────────

const appColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'appName',       label: 'App Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'osType',        label: 'OS' },
  { key: 'runtime',       label: 'Runtime' },
  { key: 'planName',      label: 'Plan' },
  {
    key: 'planTier', label: 'Tier',
    render: (row) => (row.planSkuName && row.planSkuName !== '—') ? `${row.planTier} (${row.planSkuName})` : row.planTier,
  },
  { key: 'state', label: 'Status', render: (row) => <StatusCell row={row} /> },
  { key: 'avgCpu', label: 'Avg CPU', render: (row) => <CpuUtilCell row={row} /> },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureWebAppOptimization = () => {
  const { selectedAccount } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [rows,      setRows]      = useState([]);
  const [kpi,       setKpi]       = useState([]);

  const [cpuData,        setCpuData]        = useState(null);
  const [tierData,       setTierData]       = useState(null);
  const [locationData,   setLocationData]   = useState(null);
  const [savingsData,    setSavingsData]    = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,        setTagList]        = useState([]);
  const [filterList,     setFilterList]     = useState({ tag: '', values: [] });
  const [locationFilter, setLocationFilter] = useState('');
  const [tierFilter,     setTierFilter]     = useState('');
  const [runtimeFilter,  setRuntimeFilter]  = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      appId: row.id, appName: row.appName, location: row.location, resourceGroup: row.resourceGroup,
      osType: row.osType, runtime: row.runtime, plan: row.planName, planTier: row.planTier,
      recommendedSku: row.recommendedSkuName, state: row.state, avgCpu: row.avgCpu, peakCpu: row.peakCpu,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the web app data provided, give a concise, natural insight ' +
      'directly to the user — mention the App Service plan efficiency, savings within reach, and what they ' +
      'should consider doing. Keep it conversational and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'WEB_APP_SERVICE', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchData = () => {
    if (!selectedAccount) return;
    const userId = getUserId();
    if (!userId) return;
    setIsLoading(true);
    setRows([]);
    RecommendationApiService.getAzureData({
      userId, type: 'CUSTOM_WEB_APP_SERVICE', userAccountId: selectedAccount,
      filterKey:    filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const mapped = (res?.data ?? []).map(toWebAppRow);
        setRows(mapped);
        setKpi(buildKpiData(res?.totalMonthlyCost, mapped));
      })
      .catch((err) => console.error('Error fetching web app recommendations', err))
      .finally(() => setIsLoading(false));
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag')    { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) { setFilterList({ tag: '', values: [] }); fetchData(); fetchTagList(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const locations = useMemo(() => [...new Set(rows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [rows]);
  const tiers     = useMemo(() => [...new Set(rows.map((r) => r.planTier).filter((v) => v && v !== '—'))].sort(), [rows]);
  const runtimes  = useMemo(() => [...new Set(rows.map((r) => r.runtime).filter((v) => v && v !== '—'))].sort(), [rows]);

  const filteredRows = useMemo(() => {
    let r = rows;
    if (locationFilter) r = r.filter((row) => row.location === locationFilter);
    if (tierFilter)     r = r.filter((row) => row.planTier === tierFilter);
    if (runtimeFilter)  r = r.filter((row) => (row.runtime ?? '').toLowerCase().includes(runtimeFilter.toLowerCase()));
    if (statusFilter)   r = r.filter((row) => (row.state ?? '').toLowerCase() === statusFilter.toLowerCase());
    return r;
  }, [rows, locationFilter, tierFilter, runtimeFilter, statusFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setCpuData(buildCpuHeadroom(filteredRows));
      setTierData(buildTierDonut(filteredRows));
      setLocationData(buildLocationBar(filteredRows));
      setSavingsData(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions); setInsightTotal(totalSaving);
    } else {
      setCpuData(null); setTierData(null); setLocationData(null); setSavingsData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Web App Optimization',
      kpi,
      webAppRecommendations: filteredRows,
    });
  }, [filteredRows, kpi]);

  const handleExport = () =>
    exportCSV(activeRows, appColumns.filter((c) => c.key !== 'action'), 'azure-web-app.csv');

  const clearLocalFilters = () => {
    setLocationFilter(''); setTierFilter(''); setRuntimeFilter(''); setStatusFilter('');
    setFilterList({ tag: '', values: [] });
  };

  const showClear = !!(filterList.tag || locationFilter || tierFilter || runtimeFilter || statusFilter);

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Web App</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Web App Optimization{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{rows.length} web apps</span>}
          </h1>
          <div className="meta">Azure App Service · downgrade over-provisioned plans, consolidate apps, delete idle</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={() => { fetchData(); fetchTagList(); }}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={kpi} loading={isLoading} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="CPU headroom — avg vs peak" subtitle="Low avg with idle peaks = over-provisioned plan">
          <GroupedBarChart data={cpuData} loading={isLoading} ySuffix="%" />
        </Card>
        <Card className="first" title="Cost by plan tier" subtitle="PremiumV3 / Standard / Basic / Free">
          <DonutChart data={tierData} loading={isLoading} />
        </Card>
        <TopActionsCard
          title="Top web app actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoading}
          entityName="web apps"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Plan Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Runtime</label>
          <select value={runtimeFilter} onChange={(e) => setRuntimeFilter(e.target.value)}>
            <option value="">All</option>
            {runtimes.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="Running">Running</option>
            <option value="Stopped">Stopped</option>
          </select>
        </div>
        <div className="spacer" />
        {showClear && <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>}
        <button className="btn ghost" onClick={() => { fetchData(); fetchTagList(); }}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={appColumns}
        loading={isLoading}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No web app recommendations"
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsData} loading={isLoading} />
        </Card>
        <Card className="first" title="Cost by location" subtitle="Spend distribution per region">
          <HorizontalBarChart
            data={locationData}
            loading={isLoading}
            xTickCallback={locationData && locationData._hasCost === false ? (v) => v : undefined}
          />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default AzureWebAppOptimization;
